import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'

// Register all Chart.js components
Chart.register(...registerables)

/**
 * Analytics Activity Controller
 *
 * Displays activity trend line chart and event distribution donut chart
 */
export default class extends Controller {
  static values = {
    activityUrl: String,
    distributionUrl: String
  }

  static targets = ['activityChart', 'distributionChart', 'activityLoading', 'distributionLoading']

  connect() {
    this.activityChart = null
    this.distributionChart = null
    this.loadCharts()
  }

  disconnect() {
    if (this.activityChart) this.activityChart.destroy()
    if (this.distributionChart) this.distributionChart.destroy()
  }

  async loadCharts() {
    await Promise.all([this.loadActivityChart(), this.loadDistributionChart()])
  }

  async loadActivityChart() {
    try {
      const response = await fetch(this.activityUrlValue)
      const data = await response.json()

      if (this.hasActivityLoadingTarget) {
        this.activityLoadingTarget.style.display = 'none'
      }
      if (this.hasActivityChartTarget) {
        this.activityChartTarget.style.display = 'block'
      }

      const ctx = this.activityChartTarget.getContext('2d')

      // Destroy existing chart
      if (this.activityChart) {
        this.activityChart.destroy()
      }

      this.activityChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.data.map((d) => d.date),
          datasets: [
            {
              label: 'Events',
              data: data.data.map((d) => d.count),
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: 'rgb(59, 130, 246)',
              pointBorderColor: 'white',
              pointBorderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              displayColors: false,
              callbacks: {
                title: (items) => {
                  const date = new Date(items[0].label)
                  return date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })
                },
                label: (context) => `${context.parsed.y.toLocaleString()} events`
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              ticks: {
                precision: 0,
                font: { size: 11 },
                color: '#6b7280'
              }
            },
            x: {
              type: 'time',
              time: {
                unit: 'day',
                displayFormats: {
                  day: 'MMM d'
                }
              },
              grid: { display: false },
              ticks: {
                font: { size: 11 },
                color: '#6b7280'
              }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      })
    } catch (error) {
      console.error('[AnalyticsActivity] Error loading activity chart:', error)
      if (this.hasActivityLoadingTarget) {
        this.activityLoadingTarget.textContent = 'Failed to load chart'
        this.activityLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }

  async loadDistributionChart() {
    try {
      const response = await fetch(this.distributionUrlValue)
      const data = await response.json()

      if (this.hasDistributionLoadingTarget) {
        this.distributionLoadingTarget.style.display = 'none'
      }
      if (this.hasDistributionChartTarget) {
        this.distributionChartTarget.style.display = 'block'
      }

      const ctx = this.distributionChartTarget.getContext('2d')

      // Destroy existing chart
      if (this.distributionChart) {
        this.distributionChart.destroy()
      }

      this.distributionChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.data.map((d) => d.name),
          datasets: [
            {
              data: data.data.map((d) => d.value),
              backgroundColor: data.data.map((d) => d.color),
              borderWidth: 0,
              hoverOffset: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '65%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                padding: 12,
                usePointStyle: true,
                pointStyle: 'circle',
                font: { size: 11 },
                color: '#6b7280',
                generateLabels: (chart) => {
                  const datasets = chart.data.datasets
                  return chart.data.labels.map((label, i) => ({
                    text: `${label} (${datasets[0].data[i].toLocaleString()})`,
                    fillStyle: datasets[0].backgroundColor[i],
                    strokeStyle: datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i,
                    pointStyle: 'circle'
                  }))
                }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: 'white',
              bodyColor: 'white',
              padding: 12,
              callbacks: {
                label: (context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0)
                  const percentage = ((context.parsed / total) * 100).toFixed(1)
                  return `${context.label}: ${context.parsed.toLocaleString()} (${percentage}%)`
                }
              }
            }
          }
        }
      })
    } catch (error) {
      console.error('[AnalyticsActivity] Error loading distribution chart:', error)
      if (this.hasDistributionLoadingTarget) {
        this.distributionLoadingTarget.textContent = 'Failed to load chart'
        this.distributionLoadingTarget.style.color = 'var(--color-error-600)'
      }
    }
  }
}
